import { Fragment, useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';
import {
  aedStr, ilsStr, eurStr, usdStr, pct, buildBulkExecutiveSummary, RESALE_DEFAULTS, areaStr, perSqmStr,
} from '../lib/analysisEngine';
import { CHART_COLORS as C, compact } from '../lib/uiFormat';
import { useI18n } from '../lib/i18n';

// ---------- small primitives ----------
function StatCard({ label, value, format, sub, perUnit, accent = 'gray', ils, eur, usd }) {
  const { t } = useI18n();
  const c = { green: 'text-green-600', blue: 'text-blue-600', amber: 'text-amber-600', gray: 'text-gray-900', red: 'text-red-600' }[accent] || 'text-gray-900';
  return (
    <div className="bg-white rounded-[1.75rem] border border-black/[0.06] shadow-premium p-4 flex flex-col justify-between min-h-[112px]">
      <p className="text-[11px] uppercase tracking-wide text-gray-400 font-medium leading-tight">{label}</p>
      <div>
        <p className={`text-xl md:text-2xl font-bold tabular-nums ${c} leading-tight`}>{format(value)}</p>
        {ils != null && <p className="text-[11px] text-gray-400 tabular-nums min-h-[15px]">{ilsStr((Number(value) || 0) * ils)}</p>}
        {eur != null && <p className="text-[11px] text-gray-400 tabular-nums min-h-[15px]">{eurStr((Number(value) || 0) * eur)}</p>}
        {usd != null && <p className="text-[11px] text-gray-400 tabular-nums min-h-[15px]">{usdStr((Number(value) || 0) * usd)}</p>}
        {perUnit != null && <p className="text-[11px] text-indigo-500 tabular-nums mt-0.5" title={t('per_unit_equivalent')}>≈ {format(perUnit)}{t('per_unit_suffix')}</p>}
        {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}
function SummaryCard({ label, aed, ils, eur, usd, perUnit, accent, caption }) {
  const { t } = useI18n();
  const c = { green: 'text-green-600', blue: 'text-blue-600', amber: 'text-amber-600', gray: 'text-gray-800' }[accent] || 'text-gray-800';
  return (
    <div className="rounded-xl border border-black/[0.06] p-4 bg-gray-50">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className={`text-lg font-bold tabular-nums ${c}`}>{aedStr(aed)}</p>
      <p className="text-xs text-gray-400 tabular-nums">{ilsStr((Number(aed) || 0) * ils)}</p>
      {eur != null && <p className="text-xs text-gray-400 tabular-nums">{eurStr((Number(aed) || 0) * eur)}</p>}
      {usd != null && <p className="text-xs text-gray-400 tabular-nums">{usdStr((Number(aed) || 0) * usd)}</p>}
      {perUnit != null && <p className="text-[10px] text-indigo-500 tabular-nums mt-0.5">≈ {aedStr(perUnit)}{t('per_unit_suffix')}</p>}
      {caption && <p className="text-[10px] text-gray-400 mt-1 leading-snug">{caption}</p>}
    </div>
  );
}
function AedIlsTooltip({ active, payload, label, ils, eur, usd }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white/95 backdrop-blur border border-gray-200 rounded-xl shadow-lg px-3 py-2 text-xs min-w-[150px]">
      <p className="font-semibold text-gray-700 mb-1.5 tabular-nums">{label}</p>
      {payload.filter(p => p.value != null).map((p, i) => (
        <div key={i} className="flex items-center gap-2 mb-1 last:mb-0">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: p.color || p.stroke || p.fill }} />
          <span className="text-gray-500">{p.name}</span>
          <span className="ml-auto text-right leading-tight">
            <span className="font-medium text-gray-800 block tabular-nums">{aedStr(p.value)}</span>
            <span className="text-[10px] text-gray-400 block tabular-nums">{ilsStr(p.value * ils)}</span>
            {eur != null && <span className="text-[10px] text-gray-400 block tabular-nums">{eurStr(p.value * eur)}</span>}
            {usd != null && <span className="text-[10px] text-gray-400 block tabular-nums">{usdStr(p.value * usd)}</span>}
          </span>
        </div>
      ))}
    </div>
  );
}
function PctTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white/95 backdrop-blur border border-gray-200 rounded-xl shadow-lg px-3 py-2 text-xs min-w-[130px]">
      <p className="font-semibold text-gray-700 mb-1.5 tabular-nums">{label}</p>
      {payload.filter(p => p.value != null).map((p, i) => (
        <div key={i} className="flex items-center gap-2 mb-1 last:mb-0">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: p.color || p.stroke }} />
          <span className="text-gray-500">{p.name}</span>
          <span className="ml-auto font-medium text-gray-800 tabular-nums">{pct(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

function BulkExecSummary({ b, ils, dealName }) {
  const { t, lang } = useI18n();
  const { bullets, disclaimer } = buildBulkExecutiveSummary(b, ils, dealName, lang);
  if (!bullets.length) return null;
  return (
    <section className="bg-white rounded-[1.75rem] border border-black/[0.06] shadow-premium p-5 md:p-6 mb-6">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="font-semibold text-gray-800">{t('executive_summary')}</h3>
        <span className="text-[10px] uppercase tracking-wide text-gray-400 bg-gray-50 border border-black/[0.06] rounded-full px-2 py-0.5">{t('auto_generated')}</span>
      </div>
      <ul className="space-y-2.5">
        {bullets.map((bl, i) => (
          <li key={i} className="flex gap-2.5 text-sm text-gray-600 leading-snug">
            <span className="shrink-0 text-base leading-tight" aria-hidden="true">{bl.icon}</span>
            <span>
              <span className="font-semibold text-gray-900">{bl.label}:</span>{' '}
              {bl.segments.map((s, j) => s.b
                ? <strong key={j} className="font-semibold text-gray-900 tabular-nums">{s.t}</strong>
                : <span key={j}>{s.t}</span>)}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-4 text-[11px] italic text-gray-400 leading-snug">{disclaimer}</p>
    </section>
  );
}

// Aggregate exit-scenario card (mirrors the single-unit dark card). Uses b.exitAt(n, resale)
// which applies resale costs PER UNIT (2% per sale + one NOC per unit) then sums.
function BulkExitCard({ b, ils, eur, usd, resale = RESALE_DEFAULTS, setResale, n, setN }) {
  const { t, fmtMonthYear: fmtMY } = useI18n();
  if (!b.exitCount) return null;
  const s = b.exitAt(n, resale);
  const on = !!resale.enabled;
  const setField = (k, v) => setResale && setResale({ ...resale, [k]: v });
  const yrLabel = s.n === 0 ? t('at_handover_cap') : t('plus_n_yr', { n: s.n });
  const holdText = s.n === 0 ? t('at_handover_lc') : t('x_years_after_handover', { n: s.n });
  return (
    <div className="bg-gradient-to-br from-dark to-card rounded-[1.75rem] p-5 shadow-premium text-white">
      <div className="flex items-center justify-between gap-3 mb-1">
        <h3 className="font-semibold">{t('exit_scenario_package')}</h3>
        <span className="text-[11px] text-gray-400">{t('sell_x', { date: fmtMY(s.exitDate) })}</span>
      </div>
      <p className="text-xs text-gray-400 mb-4">{t('sell_all_units', { units: b.units, hold: holdText })}</p>

      <div className="mb-4">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs text-gray-300">{t('exit_after')}</span>
          <span className="text-xs font-semibold text-amber-300">{yrLabel}</span>
        </div>
        <input type="range" min={0} max={b.exitCount - 1} step={1} value={n}
          onChange={e => setN(Number(e.target.value))} className="w-full accent-amber-400 cursor-pointer" aria-label={t('exit_year_aria')} />
        <div className="flex justify-between text-[10px] text-gray-500 mt-1"><span>{t('exit_handover')}</span><span className="tabular-nums">{t('plus_x_yr', { x: b.exitCount - 1 })}</span></div>
      </div>

      <div className="mb-4 rounded-xl bg-white/5 border border-white/10 p-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="text-sm font-medium text-gray-200">{t('include_resale_costs')}</p>
            <p className="text-[11px] text-gray-500">{t('resale_costs_per_unit_note', { units: b.units })}</p>
          </div>
          <button type="button" onClick={() => setField('enabled', !on)} aria-pressed={on}
            className={`relative w-11 h-6 rounded-full transition shrink-0 ${on ? 'bg-amber-400' : 'bg-gray-600'}`}>
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition ${on ? 'translate-x-5' : ''}`}></span>
          </button>
        </div>
        {on && (
          <div className="grid grid-cols-2 gap-3 mt-3">
            <label className="text-[11px] text-gray-400 block">{t('broker_commission_per_unit')}
              <div className="flex items-center gap-1 mt-1">
                <input type="number" step="0.1" value={resale.commissionPct} onChange={e => setField('commissionPct', e.target.value)}
                  className="w-full bg-white/10 border border-white/15 rounded-lg px-2 py-1 text-sm text-right text-white tabular-nums focus:outline-none focus:ring-1 focus:ring-amber-400" />
                <span className="text-gray-400">%</span>
              </div>
            </label>
            <label className="text-[11px] text-gray-400 block">{t('noc_fee_per_unit')}
              <div className="flex items-center gap-1 mt-1">
                <span className="text-gray-400 text-[11px]">AED</span>
                <input type="number" value={resale.nocFee} onChange={e => setField('nocFee', e.target.value)}
                  className="w-full bg-white/10 border border-white/15 rounded-lg px-2 py-1 text-sm text-right text-white tabular-nums focus:outline-none focus:ring-1 focus:ring-amber-400" />
              </div>
            </label>
          </div>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex justify-between text-sm"><span className="text-gray-400">{t('sale_value_at_exit_units', { units: b.units })}</span><span className="font-medium tabular-nums">{aedStr(s.saleValue)}</span></div>
        {s.resale && s.commission > 0 && (
          <div className="flex justify-between text-sm gap-3"><span className="text-gray-400">{t('less_broker_commission_x', { x: `${pct(resale.commissionPct)} × ${b.units}` })}</span><span className="font-medium tabular-nums text-red-300 whitespace-nowrap">−{aedStr(s.commission)}</span></div>
        )}
        {s.resale && s.noc > 0 && (
          <div className="flex justify-between text-sm gap-3"><span className="text-gray-400">{t('less_noc_fee_x', { x: `${b.units} × ${aedStr(resale.nocFee)}` })}</span><span className="font-medium tabular-nums text-red-300 whitespace-nowrap">−{aedStr(s.noc)}</span></div>
        )}
        <div className="flex justify-between text-sm gap-3"><span className="text-gray-400">{t('less_total_purchase')}</span><span className="font-medium tabular-nums text-red-300 whitespace-nowrap">−{aedStr(s.purchasePrice)}</span></div>
        {s.netRent > 0 && (
          <div className="flex justify-between text-sm gap-3"><span className="text-gray-400">{t('plus_net_rent_collected')}</span><span className="font-medium tabular-nums text-green-300 whitespace-nowrap">+{aedStr(s.netRent)}</span></div>
        )}
        <div className="border-t border-gray-700 pt-3 flex justify-between">
          <span className="text-gray-300">{t('exit_profit')} <span className="text-[11px] text-gray-500">{t('capital_gain_plus_rent')}</span></span>
          <div className="text-right">
            <span className="text-xl font-bold text-green-400 tabular-nums">{aedStr(s.profit)}</span>
            <div className="text-xs text-gray-400 tabular-nums">{ilsStr(s.profit * ils)}</div>
            {eur != null && <div className="text-xs text-gray-400 tabular-nums">{eurStr(s.profit * eur)}</div>}
            {usd != null && <div className="text-xs text-gray-400 tabular-nums">{usdStr(s.profit * usd)}</div>}
          </div>
        </div>
        <div className="flex justify-between text-[11px] text-gray-500"><span>{t('per_unit_profit_equiv')}</span><span className="tabular-nums text-indigo-300">{aedStr(b.units > 0 ? s.profit / b.units : 0)}</span></div>
        <div className="flex justify-between text-[11px] text-gray-500"><span>{t('cash_invested_pf')}</span><span className="tabular-nums">{aedStr(s.cashInvested)}</span></div>
        <div className="grid grid-cols-2 gap-2 pt-1">
          <div className="bg-white/10 rounded-xl p-3 text-center"><p className="text-[11px] text-gray-300">{t('roi_on_cash')}</p><p className="text-xl font-bold text-amber-400 tabular-nums">{pct(s.roi)}</p></div>
          <div className="bg-white/10 rounded-xl p-3 text-center"><p className="text-[11px] text-gray-300">{t('annualized_roi')}</p><p className="text-xl font-bold text-amber-400 tabular-nums">{pct(s.annualizedRoi)}</p></div>
        </div>
        <p className="text-[11px] text-gray-500 leading-snug">{s.resale ? t('resale_incl_note_bulk', { c: pct(resale.commissionPct), units: b.units, n: aedStr(resale.nocFee) }) : t('resale_excl_note')}</p>
      </div>
    </div>
  );
}

// props: bulk, ils, dealName, resale, setResale, view, presentation
export default function BulkReport({ bulk, ils, eur, usd, dealName, resale = RESALE_DEFAULTS, setResale, view = 'advanced', presentation = false }) {
  const { t, dir, fmtMonthYear: fmtMY, tMilestone } = useI18n();
  const [exitN, setExitN] = useState(0);
  const b = bulk;
  if (!b || !b.lines?.length) return null;
  const lite = view === 'lite';
  const today = new Date();
  const legendAlign = dir === 'rtl' ? 'right' : 'left';
  const heroTitle = presentation ? 'text-2xl md:text-4xl' : 'text-xl md:text-3xl';
  const handoverYear = b.atHandover?.year;
  const refLabel = b.usesMarketRef && b.usesListRef ? t('ref_market_list') : (b.usesMarketRef ? t('ref_market_value') : t('ref_list_price'));

  return (
    <>
      {/* ===== HERO — brand navy/green gradient (package spans multiple units, no single photo) ===== */}
      <section className="relative overflow-hidden rounded-[1.75rem] shadow-premium-lg mb-6 text-white">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 55%, #17513f 130%)' }} />
        <div className={`relative z-10 p-5 md:p-8 ${presentation ? 'pt-10 md:pt-16' : 'pt-8 md:pt-10'}`}>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="text-[10px] uppercase tracking-widest font-semibold bg-white/10 backdrop-blur border border-white/20 rounded-full px-2.5 py-1">{t('bulk_deal')}</span>
            {b.hasPhpp && <span className="text-[10px] uppercase tracking-widest font-semibold bg-white/10 backdrop-blur border border-white/20 rounded-full px-2.5 py-1">PHPP</span>}
            <span className="text-[10px] uppercase tracking-widest font-semibold bg-white/10 backdrop-blur border border-white/20 rounded-full px-2.5 py-1">{b.emirate === 'rak' ? t('rak') : t('dubai')}</span>
            {b.savingsVsList > 0 && <span className="text-[10px] uppercase tracking-widest font-bold bg-primary text-white rounded-full px-2.5 py-1">{t('below_list', { x: pct(b.savingsVsListPct) })}</span>}
          </div>
          <h1 className={`${heroTitle} font-bold tracking-tight leading-[1.05]`}>{dealName || t('package_purchase')}</h1>
          <p className="text-white/70 text-sm md:text-base mt-2">
            <span className="tabular-nums">{t('n_units', { n: b.units })}</span> · <span dir="ltr" className="tabular-nums">{b.lines.map(L => `${L.quantity}× ${L.unit.unit_type}`).join(' · ')}</span>
          </p>
          {b.totalSqft > 0 && (
            <p className="text-white/45 text-xs mt-1">
              {t('total_package_area', { area: areaStr(b.totalSqft) })}
              {b.pricePerSqft > 0 && <> · {t('blended_psf_psm', { psf: aedStr(b.pricePerSqft), psm: perSqmStr(b.pricePerSqft) })}</>}
            </p>
          )}
          <div className="flex flex-wrap items-end justify-between gap-4 mt-6">
            {b.handoverDate && (
              <div className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur border border-white/15 rounded-full pl-3 pr-3.5 py-1.5 text-xs text-white/60">
                <span>{t('handover')}</span>
                <span className="font-semibold text-white tabular-nums">{fmtMY(b.handoverDate)}</span>
              </div>
            )}
            <div className={`text-right shrink-0 ${!b.handoverDate ? 'ml-auto' : ''}`}>
              <p className={`${presentation ? 'text-3xl md:text-4xl' : 'text-2xl md:text-3xl'} font-bold tabular-nums leading-none`}>{aedStr(b.price)}</p>
              <p className="text-sm text-white/55 tabular-nums mt-1 min-h-[20px]">{ilsStr(b.price * ils)}</p>
              {eur != null && <p className="text-sm text-white/55 tabular-nums min-h-[20px]">{eurStr(b.price * eur)}</p>}
              {usd != null && <p className="text-sm text-white/55 tabular-nums min-h-[20px]">{usdStr(b.price * usd)}</p>}
              <p className="text-[11px] text-white/50 tabular-nums mt-0.5">≈ {aedStr(b.per(b.price))}{t('per_unit_suffix')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== EXEC SUMMARY ===== */}
      <BulkExecSummary b={b} ils={ils} eur={eur} usd={usd} dealName={dealName} />

      {/* ===== KEY NUMBERS (aggregate + per-unit equivalent) ===== */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
        <StatCard label={t('package_price')} value={b.price} format={aedStr} accent="gray" ils={ils} eur={eur} usd={usd} perUnit={b.per(b.price)} />
        <StatCard label={t('until_handover')} value={b.bucketUntil} format={aedStr} accent="amber" ils={ils} eur={eur} usd={usd} perUnit={b.per(b.bucketUntil)} />
        <StatCard label={t('on_handover')} value={b.bucketOnHandover} format={aedStr} accent="amber" ils={ils} eur={eur} usd={usd} perUnit={b.per(b.bucketOnHandover)} />
        <StatCard label={b.isStr ? t('str_net_income_yr') : t('net_rent_yr')} value={b.annualNetRent} format={aedStr} accent={b.annualNetRent >= 0 ? 'green' : 'red'} perUnit={b.per(b.annualNetRent)} sub={t('net_yield_x', { x: pct(b.netYield) })} />
        <StatCard label={t('roi_at_maturity_y', { y: b.maturityYears })} value={b.atMaturity.roi} format={pct} accent={b.atMaturity.roi >= 0 ? 'green' : 'red'} sub={t('capital_on_cash')} />
      </div>

      {/* ===== INSTANT EQUITY (discount to market) ===== */}
      {b.instantEquity !== 0 && (
        <div className="rounded-[1.75rem] p-5 md:p-6 shadow-premium text-white bg-gradient-to-br from-green-500 to-emerald-600 mb-6 ring-1 ring-green-400/40">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="font-semibold text-sm opacity-95 flex items-center gap-2"><span>⚡</span> {t('instant_equity_below_ref', { ref: refLabel })}</h3>
              <p className="text-[11px] opacity-80">{t('aggregate_day_one', { ref: refLabel })}</p>
            </div>
            <span className="text-3xl md:text-4xl font-extrabold tabular-nums">{pct(b.discountPct)}<span className="text-base font-medium"> {t('below')}</span></span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-xs opacity-80">{t('package_price')}</p>
              <p className="font-bold text-lg tabular-nums">{aedStr(b.price)}</p>
              <p className="text-[11px] opacity-75 tabular-nums">{ilsStr(b.price * ils)}</p>
              {eur != null && <p className="text-[11px] opacity-75 tabular-nums">{eurStr(b.price * eur)}</p>}
              {usd != null && <p className="text-[11px] opacity-75 tabular-nums">{usdStr(b.price * usd)}</p>}
            </div>
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-xs opacity-80">{t('reference_value_x', { ref: refLabel })}</p>
              <p className="font-bold text-lg tabular-nums">{aedStr(b.marketRefTotal)}</p>
              <p className="text-[11px] opacity-75 tabular-nums">{ilsStr(b.marketRefTotal * ils)}</p>
              {eur != null && <p className="text-[11px] opacity-75 tabular-nums">{eurStr(b.marketRefTotal * eur)}</p>}
              {usd != null && <p className="text-[11px] opacity-75 tabular-nums">{usdStr(b.marketRefTotal * usd)}</p>}
            </div>
            <div className="bg-white/20 rounded-xl p-3 col-span-2 md:col-span-1">
              <p className="text-xs opacity-90">{t('instant_equity_day1')}</p>
              <p className="font-extrabold text-xl tabular-nums">{aedStr(b.instantEquity)}</p>
              <p className="text-[11px] opacity-80">≈ {aedStr(b.per(b.instantEquity))}{t('per_unit_suffix')}</p>
            </div>
          </div>
        </div>
      )}

      {/* ===== PER-UNIT SUMMARY TABLE ===== */}
      <div className="bg-white rounded-[1.75rem] p-4 md:p-5 shadow-premium border border-black/[0.06] mb-6">
        <h3 className="font-semibold text-gray-800 mb-3">{t('units_in_package')}</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-gray-400 border-b border-black/[0.06]">
                <th className="py-2 pr-3 font-medium">{t('unit_col')}</th>
                <th className="py-2 px-3 font-medium text-right">{t('qty_col')}</th>
                <th className="py-2 px-3 font-medium text-right">{t('area_col')}</th>
                <th className="py-2 px-3 font-medium text-right">{t('list_price_col')}</th>
                <th className="py-2 px-3 font-medium text-right">{t('negotiated_price_col')}</th>
                <th className="py-2 px-3 font-medium text-right">{t('rent_yr_col')}</th>
                <th className="py-2 px-3 font-medium text-right">{t('instant_equity_col')}</th>
                <th className="py-2 pl-3 font-medium text-right">{t('line_total_col')}</th>
              </tr>
            </thead>
            <tbody>
              {b.lines.map((L, i) => (
                <tr key={i} className={`border-b border-gray-50 ${i % 2 ? 'bg-gray-50/40' : ''}`}>
                  <td className="py-2 pr-3">
                    <span className="text-gray-800 font-medium">{L.unit.unit_type}{L.unit.unit_number ? ` · ${t('unit_kw', { n: L.unit.unit_number })}` : ''}</span>
                    <span className="block text-[11px] text-gray-400">{L.project.project_name}{L.project.area ? ` · ${L.project.area}` : ''}</span>
                  </td>
                  <td className="py-2 px-3 text-right text-gray-700 tabular-nums">{L.quantity}×</td>
                  <td className="py-2 px-3 text-right text-gray-500 tabular-nums whitespace-nowrap">{((Number(L.unit.internal_sqft) || 0) + (Number(L.unit.balcony_sqft) || 0)) > 0 ? areaStr((Number(L.unit.internal_sqft) || 0) + (Number(L.unit.balcony_sqft) || 0)) : '—'}</td>
                  <td className="py-2 px-3 text-right text-gray-400 tabular-nums line-through">{aedStr(L.listPrice)}</td>
                  <td className="py-2 px-3 text-right text-gray-800 font-medium tabular-nums">{aedStr(L.effPrice)}
                    {L.hasOverride && <span className="block text-[10px] text-indigo-400">{t('negotiated')}</span>}</td>
                  <td className="py-2 px-3 text-right text-gray-600 tabular-nums">{aedStr(L.rent)}</td>
                  <td className="py-2 px-3 text-right tabular-nums">
                    <span className={L.instantEquityPer >= 0 ? 'text-green-600 font-medium' : 'text-red-500'}>{aedStr(L.instantEquityPer)}</span>
                    <span className="block text-[10px] text-gray-400">{t('vs_x', { x: L.marketRefKind })}</span>
                  </td>
                  <td className="py-2 pl-3 text-right text-gray-800 font-semibold tabular-nums">{aedStr(L.effPrice * L.quantity)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-200 font-semibold text-gray-900">
                <td className="py-2 pr-3">{t('package_total')}</td>
                <td className="py-2 px-3 text-right tabular-nums">{b.units}×</td>
                <td className="py-2 px-3 text-right tabular-nums whitespace-nowrap">{b.totalSqft > 0 ? areaStr(b.totalSqft) : '—'}</td>
                <td className="py-2 px-3 text-right text-gray-400 tabular-nums">{aedStr(b.listTotal)}</td>
                <td className="py-2 px-3 text-right tabular-nums">{aedStr(b.price)}</td>
                <td className="py-2 px-3 text-right tabular-nums">{aedStr(b.startRent)}</td>
                <td className="py-2 px-3 text-right text-green-700 tabular-nums">{aedStr(b.instantEquity)}</td>
                <td className="py-2 pl-3 text-right tabular-nums">{aedStr(b.price)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        <p className="text-[11px] text-gray-400 mt-2">{t('blended_price_sqft')}: <span className="font-medium text-gray-600 tabular-nums">{b.totalSqft > 0 ? t('psf_psm', { psf: aedStr(b.pricePerSqft), psm: perSqmStr(b.pricePerSqft) }) : '—'}</span> · {t('total_lc')} <span className="tabular-nums">{b.totalSqft > 0 ? areaStr(b.totalSqft) : '—'}</span></p>
      </div>

      {/* ===== MERGED PAYMENT SCHEDULE ===== */}
      <div className="bg-white rounded-[1.75rem] p-4 md:p-5 shadow-premium border border-black/[0.06] mb-6">
        <h3 className="font-semibold text-gray-800 mb-4">{t('combined_payment_schedule')} <span className="text-[11px] font-normal text-gray-400">— {t('all_units_merged')}</span></h3>
        {!lite && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="text-left text-gray-400 border-b border-black/[0.06]">
                  <th className="py-2 pr-3 font-medium">{t('milestone')}</th>
                  <th className="py-2 px-3 font-medium">{t('units_col')}</th>
                  <th className="py-2 px-3 font-medium text-right">{t('amount')}</th>
                  <th className="py-2 px-3 font-medium">{t('due')}</th>
                  <th className="py-2 px-3 font-medium text-right">{t('cumulative')}</th>
                  <th className="py-2 pl-3 font-medium text-right">{t('cum_pct')}</th>
                </tr>
              </thead>
              <tbody>
                {b.schedule.map((r, i) => {
                  const prev = b.schedule[i - 1];
                  const showToday = !!(prev && prev.date < today && r.date >= today);
                  return (
                    <Fragment key={i}>
                      {showToday && (
                        <tr aria-hidden="true"><td colSpan={6} className="p-0">
                          <div className="flex items-center gap-2 py-1"><div className="h-px flex-1 bg-rose-300" /><span className="text-[10px] font-semibold text-rose-500 uppercase tracking-wide">{t('today')}</span><div className="h-px flex-1 bg-rose-300" /></div>
                        </td></tr>
                      )}
                      <tr className={`border-b border-gray-50 ${r.isFee ? 'bg-amber-50/50' : (i % 2 ? 'bg-gray-50/40' : '')}`}>
                        <td className="py-2 pr-3">
                          <span className={r.isFee ? 'text-amber-700' : 'text-gray-800'}>{r.isFee ? '🧾 ' : ''}{tMilestone(r.label)}</span>
                          {r.isHandover && <span className="ml-2 text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">{t('badge_handover')}</span>}
                          {r.isPost && <span className="ml-2 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">PHPP</span>}
                        </td>
                        <td className="py-2 px-3 text-gray-500 text-xs whitespace-nowrap tabular-nums">{r.units}</td>
                        <td className="py-2 px-3 text-right tabular-nums text-gray-800">{aedStr(r.amount)}<span className="block text-[10px] text-gray-400">{ilsStr(r.amount * ils)}</span>{eur != null && <span className="block text-[10px] text-gray-400">{eurStr(r.amount * eur)}</span>}{usd != null && <span className="block text-[10px] text-gray-400">{usdStr(r.amount * usd)}</span>}</td>
                        <td className="py-2 px-3 text-gray-500 whitespace-nowrap tabular-nums">{fmtMY(r.date)}</td>
                        <td className="py-2 px-3 text-right tabular-nums text-gray-700 font-medium">{aedStr(r.cumAED)}<span className="block text-[10px] text-gray-400 font-normal">{ilsStr(r.cumAED * ils)}</span>{eur != null && <span className="block text-[10px] text-gray-400 font-normal">{eurStr(r.cumAED * eur)}</span>}{usd != null && <span className="block text-[10px] text-gray-400 font-normal">{usdStr(r.cumAED * usd)}</span>}</td>
                        <td className="py-2 pl-3 text-right text-gray-500 tabular-nums">{pct(r.cumPct)}</td>
                      </tr>
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className={`grid grid-cols-1 gap-4 mt-5 ${b.hasPhpp ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-3'}`}>
          <SummaryCard label={t('until_handover')} aed={b.bucketUntil} ils={ils} eur={eur} usd={usd} perUnit={b.per(b.bucketUntil)} accent="amber" />
          <SummaryCard label={t('on_handover')} aed={b.bucketOnHandover} ils={ils} eur={eur} usd={usd} perUnit={b.per(b.bucketOnHandover)} accent="amber" />
          {b.hasPhpp && <SummaryCard label={t('post_handover_phpp')} aed={b.bucketPost} ils={ils} eur={eur} usd={usd} perUnit={b.per(b.bucketPost)} accent="blue" />}
          <SummaryCard label={t('grand_total_incl_fees')} aed={b.grandTotal} ils={ils} eur={eur} usd={usd} perUnit={b.per(b.grandTotal)} accent="gray" />
        </div>
      </div>

      {/* ===== CASH FLOW (peak + break-even + cumulative position) ===== */}
      {b.cashFlow && (
        <div className="bg-white rounded-[1.75rem] p-4 md:p-5 shadow-premium border border-black/[0.06] mb-6">
          <h3 className="font-semibold text-gray-800 mb-1">{t('aggregate_cash_flow')}</h3>
          <p className="text-[11px] text-gray-400 mb-4">{t('cf_ledger_note')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div className="rounded-xl border border-rose-100 bg-rose-50/60 p-4">
              <p className="text-xs text-rose-500 mb-1">{t('peak_cash_requirement')}</p>
              <p className="text-xl font-bold text-rose-700 tabular-nums">{aedStr(b.cashFlow.peak.magnitude)}</p>
              <p className="text-[11px] text-rose-400">{t('on_x', { x: b.cashFlow.peak.label })} · ≈ {aedStr(b.per(b.cashFlow.peak.magnitude))}{t('per_unit_suffix')}</p>
            </div>
            <div className="rounded-xl border border-green-100 bg-green-50/60 p-4">
              <p className="text-xs text-green-600 mb-1">{t('cash_break_even')}</p>
              <p className="text-xl font-bold text-green-700 tabular-nums">{b.cashFlow.breakEvenReached ? b.cashFlow.breakEven.monthLabel : t('gt_x_y', { x: b.maturityYears })}</p>
              <p className="text-[11px] text-green-500">{t('cum_pos_turns_positive')}</p>
            </div>
            <div className="rounded-xl border border-black/[0.06] bg-gray-50 p-4">
              <p className="text-xs text-gray-500 mb-1">{t('net_position_plus_y', { y: b.maturityYears })}</p>
              <p className="text-xl font-bold text-gray-800 tabular-nums">{aedStr(b.cashFlow.endCumulative)}</p>
              <p className="text-[11px] text-gray-400">{t('rent_in_cash_out')}</p>
            </div>
          </div>
          {!lite && (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={b.cashFlow.series} margin={{ top: 8, right: 12, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="gBulkCash" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity={0.35} /><stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" minTickGap={28} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={compact} width={48} />
                <Tooltip content={<AedIlsTooltip ils={ils} eur={eur} usd={usd} />} />
                <ReferenceLine y={0} stroke="#e5e7eb" />
                <Area type="monotone" dataKey="cumulative" name={t('cumulative_cash_position')} stroke="#6366f1" strokeWidth={2} fill="url(#gBulkCash)" isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

      {/* ===== ROI TABLE + EXIT ===== */}
      <div className={`grid gap-6 mb-6 ${lite ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-3'}`}>
        {!lite && (
          <div className="bg-white rounded-[1.75rem] p-4 md:p-5 shadow-premium border border-black/[0.06] lg:col-span-2">
            <h3 className="font-semibold text-gray-800">{t('return_on_investment_package')}</h3>
            <p className="text-[11px] text-gray-400 mb-4">{t('roi_package_note')}</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-black/[0.06]">
                    <th className="py-2 pr-3 font-medium">{t('horizon')}</th>
                    <th className="py-2 px-3 font-medium text-right">{t('property_value')}</th>
                    <th className="py-2 px-3 font-medium text-right">{t('cash_deployed')}</th>
                    <th className="py-2 px-3 font-medium text-right">{t('capital_roi')}</th>
                    <th className="py-2 px-3 font-medium text-right">{t('cum_net_rent')}</th>
                    <th className="py-2 pl-3 font-medium text-right">{t('combined_roi')}</th>
                  </tr>
                </thead>
                <tbody>
                  {[[t('at_handover'), b.atHandover], [t('plus_y_midpoint', { y: b.midYears }), b.atMid], [t('plus_y_maturity', { y: b.maturityYears }), b.atMaturity]].map(([lbl, row]) => row && (
                    <tr key={lbl} className="border-b border-gray-50 align-top">
                      <td className="py-2 pr-3 text-gray-800 font-medium">{lbl}</td>
                      <td className="py-2 px-3 text-right tabular-nums text-gray-800">{aedStr(row.value)}<span className="block text-[10px] text-indigo-400">≈ {aedStr(b.per(row.value))}{t('per_u_suffix')}</span></td>
                      <td className="py-2 px-3 text-right text-gray-500 tabular-nums">{aedStr(row.cashDeployed)}<span className="block text-[10px] text-indigo-400">≈ {aedStr(b.per(row.cashDeployed))}{t('per_u_suffix')}</span></td>
                      <td className="py-2 px-3 text-right text-green-600 font-medium tabular-nums">{pct(row.roi)}</td>
                      <td className="py-2 px-3 text-right text-gray-700 tabular-nums">{aedStr(row.cumNetRent)}<span className="block text-[10px] text-indigo-400">≈ {aedStr(b.per(row.cumNetRent))}{t('per_u_suffix')}</span></td>
                      <td className="py-2 pl-3 text-right text-blue-600 font-semibold tabular-nums">{pct(row.combined)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-sm">
              <div className="bg-gray-50 rounded-xl p-3 text-center"><p className="text-xs text-gray-400 mb-0.5">{t('gross_yield')}</p><p className="font-bold text-gray-800 tabular-nums">{pct(b.grossYield)}</p></div>
              <div className="bg-gray-50 rounded-xl p-3 text-center"><p className="text-xs text-gray-400 mb-0.5">{t('net_yield')}</p><p className="font-bold text-gray-800 tabular-nums">{pct(b.netYield)}</p></div>
              <div className="bg-gray-50 rounded-xl p-3 text-center"><p className="text-xs text-gray-400 mb-0.5">{t('coc_yield')}</p><p className="font-bold text-gray-800 tabular-nums">{pct(b.cashYield)}</p></div>
              <div className="bg-gray-50 rounded-xl p-3 text-center"><p className="text-xs text-gray-400 mb-0.5">{t('combined_roi_ann')}</p><p className="font-bold text-gray-800 tabular-nums">{pct(b.combinedMaturityAnnual)}</p></div>
            </div>
          </div>
        )}
        <BulkExitCard b={b} ils={ils} eur={eur} usd={usd} resale={resale} setResale={setResale} n={exitN} setN={setExitN} />
      </div>

      {/* ===== CHARTS ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-[1.75rem] p-4 md:p-5 shadow-premium border border-black/[0.06]">
          <h3 className="font-semibold text-gray-800 text-sm mb-3">{t('package_value_vs_invested')}</h3>
          {b.years.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={b.years} margin={{ top: 8, right: 12, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={compact} width={48} />
                <Tooltip content={<AedIlsTooltip ils={ils} eur={eur} usd={usd} />} />
                <Legend wrapperStyle={{ fontSize: 12 }} align={legendAlign} />
                {handoverYear != null && <ReferenceLine x={handoverYear} stroke={C.handover} strokeDasharray="4 3" />}
                <Line type="monotone" dataKey="value" name={t('package_value_series')} stroke={C.value} strokeWidth={2} dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="invested" name={t('total_invested')} stroke={C.invested} strokeWidth={2} dot={false} strokeDasharray="5 4" isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : <div className="h-[260px] flex items-center justify-center text-sm text-gray-300">{t('no_data')}</div>}
        </div>
        <div className="bg-white rounded-[1.75rem] p-4 md:p-5 shadow-premium border border-black/[0.06]">
          <h3 className="font-semibold text-gray-800 text-sm mb-3">{t('combined_roi_over_time')}</h3>
          {b.years.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={b.years} margin={{ top: 8, right: 12, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => v.toFixed(0) + '%'} width={44} />
                <Tooltip content={<PctTooltip />} />
                {handoverYear != null && <ReferenceLine x={handoverYear} stroke={C.handover} strokeDasharray="4 3" />}
                <ReferenceLine y={0} stroke="#e5e7eb" />
                <Line type="monotone" dataKey="combinedROI" name={t('combined_roi_series')} stroke={C.roi} strokeWidth={2.5} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : <div className="h-[260px] flex items-center justify-center text-sm text-gray-300">{t('no_data')}</div>}
        </div>
      </div>
    </>
  );
}
